import { supabase } from "../supabase/SupabaseClient";
import { traerTodas } from "./paginar";

/* Lo que sabe RRHH: la nómina de sueldos y el historial laboral.
 *
 * ── CADA CONSULTA PIDE SU PERMISO, Y LO PIDE LA BASE ────────────────────────
 *
 *   `traerChoferes`    → `choferes_activos`, con `ver.gente`. Nombre, empresa,
 *                        base y legajo: nada de RRHH.
 *   `nominaDe`         → `nomina`, con `ver.rrhh`. Antigüedad, obra social,
 *                        categoría, motivo de egreso.
 *   `traerNomina`      → la misma tabla y la misma llave, entera.
 *   `traerDiferencias` → `nomina_vs_padron`, que se apoya en `nomina`: sin
 *                        `ver.rrhh` la vista queda vacía sola.
 *   `periodosDe`       → `persona_periodos`, que es una vista de
 *                        `personas.periodos`. No agrega alcance: ese texto ya
 *                        se lee desde el padrón. Lo único que hace es parsearlo.
 *
 * Ninguna chequea el permiso acá. Sin la llave, Supabase devuelve
 * **cero filas y no un error** —así están escritas las policies— y cada
 * pantalla decide si eso es "no tenés permiso" o "esta persona no está". Que
 * la que decida sea la base y no el front es lo que hace que escribir la URL a
 * mano no sirva de nada.
 *
 * ── POR QUÉ NO PASAN POR `useRealtime` ──────────────────────────────────────
 *
 * El store de realtime mantiene en memoria los padrones que se editan desde la
 * app. Nada de esto se edita desde la app: la nómina la escribe
 * `scripts/importar-nomina.py` desde el archivo que pasa Erick, y el historial
 * es una vista de sólo lectura. Meterlos ahí sería prometer que se pueden
 * tocar.
 */

/* La nómina de UNA persona, para su ficha. `null` si no está.
 *
 * NO ESTAR ES NORMAL Y NO ES UN ERROR: la nómina son 292 personas sobre las
 * 2.476 del padrón. También vuelve `null` sin `ver.rrhh`, y la ficha distingue
 * los dos casos con el permiso que ya tiene a mano.
 */
export async function nominaDe(dni) {
  const d = String(dni ?? "").replace(/\D/g, "");
  if (!d) return null;
  const { data, error } = await supabase
    .from("nomina")
    .select(
      "dni,empresa,legajo,apellido,nombres," +
        "ingreso_real,ingreso_computable,categoria,calificacion," +
        "convenio,contrato,destino,obra_social," +
        "trabaja,egreso,motivo_egreso,periodo,es_chofer",
    )
    .eq("dni", d)
    .limit(1);
  if (error) throw error;
  return data?.[0] ?? null;
}

/* El historial laboral de una persona, ordenado del más viejo al más nuevo.
 *
 * SE ORDENA ACÁ PORQUE EL ORIGEN NO ESTÁ ORDENADO: adentro del JSON los
 * períodos vienen en cualquier orden, y una historia laboral desordenada se
 * lee como si la persona hubiera entrado y salido a los saltos.
 *
 * `orden` desempata a los que comparten `inicio` o no lo tienen —son 276 sin
 * fecha de inicio— para que dos visitas a la misma ficha no muestren las filas
 * en distinto orden.
 */
export async function periodosDe(dni) {
  const d = String(dni ?? "").replace(/\D/g, "");
  if (!d) return [];
  const { data, error } = await supabase
    .from("persona_periodos")
    .select(
      "dni,orden,inicio,fin,empresa,cargado_por,detalle," +
        "detalle_original,abierto,fechas_invertidas",
    )
    .eq("dni", d)
    .order("inicio", { ascending: true, nullsFirst: false })
    .order("orden", { ascending: true });
  if (error) throw error;
  return data || [];
}

/* Los choferes de la nómina activa. Son 170.
 *
 * Vienen de la nómina y NO del padrón a propósito: `personas` tiene 2.476
 * filas con `estado` heredado de Firestore que da 356 activos, y ahí adentro
 * hay DNI inventados, proveedores y bajas que nadie registró. La nómina es
 * contra lo que se paga.
 */
export async function traerChoferes() {
  return traerTodas(() =>
    supabase
      .from("choferes_activos")
      .select("dni,apellido,nombres,empresa,destino,legajo,calificacion", {
        count: "exact",
      })
      .order("apellido", { ascending: true })
      .order("nombres", { ascending: true })
      // Dos personas pueden llamarse igual: sin el DNI el orden empata y la
      // paginación se saltea filas.
      .order("dni", { ascending: true }),
  );
}

/* Todo el personal activo, no sólo los choferes. Son 292.
 *
 * HERMANA DE `traerChoferes`, un escalón más ancha: la misma vista sin el
 * filtro `es_chofer`, con las mismas columnas no sensibles y la misma llave
 * `ver.gente`. La usa el picker de «cargar novedad» de RRHH, que tiene que
 * poder encontrar a un gomero o a un administrativo — no sólo a los que
 * manejan (P-167). El alta de infracciones se queda con `traerChoferes`: una
 * infracción de manejo es de un chofer.
 *
 * Trae `es_chofer` para que la pantalla pueda distinguir sin abrir la nómina.
 * Sin `ver.gente` la policy de la vista devuelve cero filas, no un error.
 */
export async function traerPersonal() {
  return traerTodas(() =>
    supabase
      .from("personal_activo")
      .select(
        "dni,apellido,nombres,empresa,destino,legajo,calificacion,es_chofer",
        { count: "exact" },
      )
      .order("apellido", { ascending: true })
      .order("nombres", { ascending: true })
      // Dos personas pueden llamarse igual: sin el DNI el orden empata y la
      // paginación se saltea filas.
      .order("dni", { ascending: true }),
  );
}

/* La nómina entera: los 292 que cobran este mes.
 *
 * TRAE TAMBIÉN A LOS QUE YA NO TRABAJAN, y la pantalla los separa. Hoy son
 * cero —los cuatro archivos que mandó Erick son de activos— pero el día que
 * llegue un export con bajas, `nomina_cerrar_faltantes` las va a marcar y esta
 * consulta tiene que poder mostrarlas: es el único lugar del hub donde se va a
 * poder leer un motivo de egreso.
 *
 * Sin `ver.rrhh` la policy devuelve cero filas, no un error. La pantalla lo
 * distingue con el permiso que ya tiene a mano.
 */
export async function traerNomina() {
  return traerTodas(() =>
    supabase
      .from("nomina")
      .select(
        "dni,cuil,empresa,legajo,apellido,nombres," +
          "ingreso_real,ingreso_computable,categoria,calificacion," +
          "convenio,contrato,destino,trabaja,egreso,motivo_egreso," +
          "periodo,es_chofer",
        { count: "exact" },
      )
      .order("apellido", { ascending: true })
      .order("nombres", { ascending: true })
      .order("dni", { ascending: true }),
  );
}

/* Las diferencias entre la nómina y el padrón. Son 92.
 *
 * NINGUNA SE APLICA SOLA, y por eso esto es un `select` y no hay un botón de
 * «corregir». Cada fila es una decisión de RRHH: si el que cobra y figura de
 * baja es un reingreso que nadie cargó, o si el que el padrón da activo y no
 * cobra se fue en marzo. Resolverlas de este lado sería adivinar 92 veces.
 *
 * La vista es `security_invoker`, así que sin `ver.rrhh` vuelve vacía.
 */
export async function traerDiferencias() {
  return traerTodas(() =>
    supabase
      .from("nomina_vs_padron")
      .select(
        "caso,dni,empresa,legajo,quien,estado_padron," +
          "categoria,calificacion,es_chofer",
        { count: "exact" },
      )
      .order("caso", { ascending: true })
      .order("quien", { ascending: true })
      .order("dni", { ascending: true }),
  );
}
